/**
 * Yappa: Product card alternate image loader.
 *
 * Secondary product images are NOT rendered into the DOM on page load.
 * They live inside a <template data-secondary-image> element, which browsers
 * do not fetch resources for.
 *
 * Lifecycle:
 *   1. On hover (desktop/mouse only), we clone the template into the card so
 *      the browser downloads the secondary image immediately. It's still
 *      hidden (no `.is-visible` class) — just preloaded and ready.
 *   2. Click of the arrow button toggles the `.is-visible` class on the
 *      already-preloaded secondary. Transition is instant because the image
 *      is already cached/rendered.
 *   3. Once the user's mouse leaves the card, if the secondary is visible
 *      we remove `.is-visible` so the card reverts to the primary image.
 *      (The preloaded node stays in the DOM — tiny cost, instant on re-hover.)
 *
 * A `data-busy` guard on the arrow button prevents double-firing when events
 * bubble through multiple listeners.
 *
 * Delegation uses `pointerover` / `pointerout` (which DO bubble, unlike
 * `pointerenter` / `pointerleave`) with a relatedTarget containment check
 * so we only react to actual boundary crossings of the card media area.
 */
(function () {
  'use strict';

  // Early return on pages without any product cards (cart, account,
  // blog, 404, /pages/sell, checkout, etc.). Saves the ~4KB parse
  // cost and avoids registering a bubbling click listener that will
  // never match. Safe because in this theme, product cards are
  // server-rendered on collection/PDP/home templates — facet updates
  // re-render sections IN-place on pages that already have cards
  // (and where this script has already initialised).
  if (!document.querySelector('.product-card__media')) return;

  function ensurePreloaded(media) {
    if (media.querySelector('.product-card__secondary')) return;
    var tpl = media.querySelector('template[data-secondary-image]');
    if (!tpl || !tpl.content) return;
    var anchor = media.querySelector('a.media');
    if (!anchor) return;
    anchor.appendChild(tpl.content.cloneNode(true));
  }

  function setVisible(media, visible) {
    var secondary = media.querySelector('.product-card__secondary');
    if (!secondary) return;
    secondary.classList.toggle('is-visible', visible);
    var button = media.querySelector('[data-alt-toggle]');
    if (button) button.setAttribute('aria-pressed', visible ? 'true' : 'false');
  }

  function toggleButton(button) {
    if (button.dataset.busy === '1') return;
    button.dataset.busy = '1';
    setTimeout(function () { delete button.dataset.busy; }, 50);

    var media = button.closest('.product-card__media');
    if (!media) return;

    // Belt-and-braces: if preload somehow didn't happen (keyboard focus,
    // touch, or some edge case where pointerover never fired), clone now.
    ensurePreloaded(media);

    var secondary = media.querySelector('.product-card__secondary');
    if (!secondary) return;
    setVisible(media, !secondary.classList.contains('is-visible'));
  }

  // Click the arrow → toggle visible class on the (already preloaded) clone.
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-alt-toggle]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    toggleButton(button);
  }, true); // capture phase — beat theme default listeners

  // Hover in → preload the secondary image so the arrow toggle is instant.
  document.addEventListener('pointerover', function (event) {
    if (event.pointerType !== 'mouse') return;
    var media = event.target.closest && event.target.closest('.product-card__media');
    if (!media) return;
    // Only fire when we're crossing INTO the media from outside.
    if (event.relatedTarget && media.contains(event.relatedTarget)) return;
    ensurePreloaded(media);
  });

  // Hover out → if the secondary is currently showing, revert to primary.
  document.addEventListener('pointerout', function (event) {
    if (event.pointerType !== 'mouse') return;
    var media = event.target.closest && event.target.closest('.product-card__media');
    if (!media) return;
    // Only fire when we're actually leaving the media (not moving within it).
    if (event.relatedTarget && media.contains(event.relatedTarget)) return;
    setVisible(media, false);
  });
})();
